// Which PARENT SLOT a person takes — father or mother — for the children they have
// with a given partner (TGCLOUD-1786 Phase 1, decision D2).
//
// The slot follows REPRODUCTIVE SEX, not gender identity. Before this, the FHQ
// topology derivations (validatePedigreeConfig here, deriveGraphFromRelationships on
// the server) chose it from genderIconMap / resolveGender — maps that read
// `genderIdentity || gender` and exist to pick the SYMBOL. Only 'female' went to the
// mother slot, so a proband recorded as sex female with identity "Non-binary/gender
// diverse - AFAB" became the FATHER of her children and her male partner their
// MOTHER. The chart looked right (the symbol follows identity) while the inheritance
// analyser, the CanRisk/GA4/Phenopacket exports and the add-child / link-sibling
// flows all read the inverted roles.
//
// genderIconMap stays the SYMBOL map. This module is the only thing that decides a
// slot, and it is used by all three writers so they cannot disagree:
//   - validatePedigreeConfig (the chart's first-open FHQ derivation, utils.js)
//   - resolveChildParents (the native add-child, edgeIntegrity.js)
//   - deriveGraphFromRelationships (server FHQ auto-apply / self-submit) through the
//     SERVER copy, backend/util/reproductiveSex.js — the backend cannot import from
//     src/, so there are two copies. They are bound by ONE vector table,
//     backend/__tests__/fixtures/reproductiveSexVectors.json, which both the vitest
//     and the jest suites consume, plus a vitest test that runs the two copies side
//     by side. Change a rule here and change it there too.
//
// Deliberately import-free: edgeIntegrity.js keeps a leaves-only import list.

export const SEX = Object.freeze({
  male: 'male',
  female: 'female',
  unknown: 'unknown'
});

export const PARENT_SLOT = Object.freeze({
  father: 'father',
  mother: 'mother'
});

// Gender identity → sex assigned at birth. The same table as `genderMappings` in
// src/components/Constants.js and src/components/questionnaire/Constants.js, which
// disagree on one key: "Unknown/Not Stated" is spelled three ways across the app —
// 'Unknown/Not Stated ' (Constants.js option value, trailing space), 'Unknown/Not
// Stated' (questionnaire option value) and 'Unknown/Not Stated Unknown' (both maps'
// key, and what the GA4/JSON import writes) — so neither real option value matched
// either map. Keys are compared after normalisation (trimmed, inner whitespace
// collapsed, lower-cased), so all three spellings land here.
const IDENTITY_TO_SEX = {
  male: SEX.male,
  female: SEX.female,
  indeterminate: SEX.unknown,
  'male - afab': SEX.female,
  'male - uaab': SEX.unknown,
  'female - amab': SEX.male,
  'female - uaab': SEX.unknown,
  'non-binary/gender diverse - afab': SEX.female,
  'non-binary/gender diverse - amab': SEX.male,
  'non-binary/gender diverse - uaab': SEX.unknown,
  'unknown/not stated': SEX.unknown,
  'unknown/not stated unknown': SEX.unknown
};

const normKey = (v) =>
  typeof v === 'string' ? v.trim().replace(/\s+/g, ' ').toLowerCase() : '';

// 'male' | 'female' | 'unknown' from a bare sex string, any case. Anything else —
// null, '', 'unknown', a stray identity label — is unknown.
const normaliseSex = (v) => {
  const k = normKey(v);
  if (k === SEX.male) return SEX.male;
  if (k === SEX.female) return SEX.female;
  return SEX.unknown;
};

/**
 * A person's reproductive sex: the recorded `gender` when it is male or female
 * (case-insensitive), else the sex the recorded `genderIdentity` implies, else
 * 'unknown'. `gender` wins over identity because it is the field the app records as
 * Sex Assigned at Birth; identity is only consulted when sex was not recorded.
 * @param {{gender?: string, genderIdentity?: string}|null|undefined} person
 * @returns {'male'|'female'|'unknown'}
 */
export const reproductiveSexOf = (person) => {
  if (!person) return SEX.unknown;
  const recorded = normaliseSex(person.gender);
  if (recorded !== SEX.unknown) return recorded;
  return IDENTITY_TO_SEX[normKey(person.genderIdentity)] || SEX.unknown;
};

/**
 * The parent slot a person of `selfSex` takes with a partner of `partnerSex` (D2):
 * male → father, female → mother; UNKNOWN takes the complement of the partner's sex
 * (partner male → mother, partner female → father); both unknown — or no partner at
 * all — → father. The partner fills the other slot.
 * @param {string} selfSex     'male' | 'female' | 'unknown' (any case; else unknown)
 * @param {string|null} partnerSex  the same, or null/undefined for no partner
 * @returns {'father'|'mother'}
 */
export const parentSlotFor = (selfSex, partnerSex) => {
  const self = normaliseSex(selfSex);
  if (self === SEX.male) return PARENT_SLOT.father;
  if (self === SEX.female) return PARENT_SLOT.mother;
  const partner = normaliseSex(partnerSex);
  if (partner === SEX.male) return PARENT_SLOT.mother;
  return PARENT_SLOT.father;
};

/**
 * The ONE slot a person holds across ALL their unions (TGCLOUD-1786, decided by
 * Tim). A known sex decides it, as parentSlotFor does. An UNKNOWN-sex person keeps
 * the slot their existing children already give them (`childrenSlot`), whichever
 * partner the next child is with; with no children yet it is D2 against their
 * FIRST partner (`firstPartnerSex`), and father with no partner at all.
 *
 * Before this, D2 was applied per couple, so an unknown-sex person with a male and
 * a female partner was the mother of one sibship and the father of the other — one
 * person in both roles. Each writer picks the first partner itself, the same way:
 *   - native (edgeIntegrity.js): the first entry of spouseIds that resolves;
 *   - FHQ (validatePedigreeConfig, deriveGraphFromRelationships): the first member
 *     labelled `spouse`, in member order, else the linked biological parent of the
 *     first child that names one.
 * A later partner whose known sex contradicts the slot is written into the other
 * slot by the FHQ derivations (as any same-sex couple always has been) and left out
 * of the child's slots by the native add-child (as a same-sex spouse always has been).
 * @param {string} selfSex          'male' | 'female' | 'unknown' (any case)
 * @param {string|null} firstPartnerSex  the first partner's sex, or null for none
 * @param {'father'|'mother'|null} childrenSlot  the slot existing children give them
 * @returns {'father'|'mother'}
 */
export const parentSlotAcrossUnions = (
  selfSex,
  firstPartnerSex,
  childrenSlot = null
) => {
  if (normaliseSex(selfSex) !== SEX.unknown)
    return parentSlotFor(selfSex, null);
  if (
    childrenSlot === PARENT_SLOT.father ||
    childrenSlot === PARENT_SLOT.mother
  )
    return childrenSlot;
  return parentSlotFor(selfSex, firstPartnerSex);
};

// The other slot of a couple.
export const otherParentSlot = (slot) =>
  slot === PARENT_SLOT.mother ? PARENT_SLOT.father : PARENT_SLOT.mother;

// The sex a slot implies — the gender to give an INVENTED partner (a dummy) so that
// it is consistent with the slot it is put in.
export const sexForParentSlot = (slot) =>
  slot === PARENT_SLOT.mother ? SEX.female : SEX.male;
